import AccessDeniedPage from "./access-denied-page/AccessDeniedPage";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import { API_BASE_URL } from "settings";
import { logout } from "utils/api";

interface AuthState {
  /** Whether any url calls are being waited on. */
  loading: boolean;
  /** The email address of the user. */
  email?: string;
  /** If authentication failes for some reason this will be true. */
  failed: boolean;
  /** Whether the user has consented to terms and conditions */
  hasConsented: boolean;
  /** Has the user been authorzied */
  isAuthorized: boolean;
}

/** Checks to see if the response body from /user-email contains an email attribute. */
const isEmailResponse = (
  body: unknown
): body is { email: string; first_name: string; last_name: string; groups: boolean; has_accepted_terms: boolean } => {
  return Boolean(body && typeof body === "object" && "email" in body && "groups" in body);
};

/** Provider for a openid connect confidential token workflow to authentication.  */
const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [{ loading, email, failed, hasConsented, isAuthorized }, setState] = useState<AuthState>({
    loading: true,
    failed: false,
    hasConsented: false,
    isAuthorized: false,
  });
  const navigateTo = useNavigate();
  const redirectBrowser = (path: string) => {
    API_BASE_URL ? window.location.replace(`${API_BASE_URL}${path}`) : navigateTo(path);
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}auth/user-details`, { credentials: "include" })
      .then(async (response) => {
        if (response.ok) {
          const body = await response.json();
          if (isEmailResponse(body)) {
            setState({
              loading: false,
              email: body.email,
              failed: false,
              hasConsented: body["has_accepted_terms"],
              isAuthorized: body["groups"],
            });
          } else {
            // TODO rollbar error logging - this will be handled in ErrorBoundary
            console.error("Failed to log in user due to unexpected response body", body);
            setState({
              loading: false,
              failed: true,
              hasConsented: false,
              isAuthorized: false,
            });
          }
        } else {
          // remove error message being logged to console.
          if (response.status === 401) {
            console.error("You are not authorized. Unable to Login");
          }
          setState({ loading: false, failed: true, hasConsented: false, isAuthorized: false });
        }
      })
      .catch(() => {
        // Don't log this one to the rollbar ect as it is fired everytime a user opens the page and is not logged in.
        // TODO check for specific error to not being logged in (this should be a redirect failure as per fetch config)
        setState({ loading: false, failed: true, hasConsented: false, isAuthorized: false });
      });
  }, []);

  if (email && !isAuthorized) {
    return <AccessDeniedPage appName="Adapt" email={email || undefined} onLogout={logout} />;
  }

  return (
    <AuthContext.Provider
      value={{
        loading,
        failed,
        email,
        resetAuth: () =>
          setState({
            loading: false,
            failed: false,
            email: undefined,
            isAuthorized: false,
            hasConsented: false,
          }),
        isAuthenticated: Boolean(email),
        hasConsented,
        isAuthorized,
        loginWithRedirect: (redirectUrl: string) => redirectBrowser(`auth/login?redirect_uri=${redirectUrl}`),
        logout: () => redirectBrowser(`auth/logout?redirect_url=${window.location.href}`),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
